import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Loader from "components/loader";
import { CREATE_LINE_ITEM, TOGGLE_UPDATING_SUB_PRODUCT, TOGGLE_SAVE_LOADING, SET_HIGHLIGHT_BANNER } from "utils/action-types";
import handleBeforeUnload from "src/hooks/useConfirmLeave";
import { useGlobalContext } from "context/global";
import { addLineOnce } from "actions/account/subscription";
import clsx from "clsx/lite";
import useSaveToast from "hooks/useSaveToast";
import { trackAddToSubscriptionCart } from "services/analytics";
import { lifeTimeOfferKey } from "utils/constants";
import Tooltip from "components/tooltip";
import obtainOrderInfo from "utils/obtain-order-info";

const AddToSubscriptionCart = ({
	productVariantId, stock, className, product, text, onToastClick, callback, isUpsell = false, backgroundColor = "#000"
}) => {
	const {
		isActive, lines, linesToCreate, isCartOpen, pendingChanges,
		isStickyCartOpen, saveInProgress, nextBillingDate, chargeOffsetDays,
	} = useSelector((state) => state.account.subscription);

	const { translation, isSubscriptionPage, isCollection } = useGlobalContext();

	const { tags: customerTags, latestSubOrder } = useSelector((state) => state.account.profile);

	const { lifetimeOffer = {} } = product || {};

	const isLimittedOffer = (lifetimeOffer?.enabled && customerTags.includes(lifetimeOffer.customerTag));
	const { isDispatched } = obtainOrderInfo(nextBillingDate, chargeOffsetDays, latestSubOrder);

	if (!isActive) {
		return null;
	}

	const toggleLoading = () => {
		dispatch({ type: TOGGLE_UPDATING_SUB_PRODUCT, productVariantId, stock });
		dispatch({ type: TOGGLE_SAVE_LOADING });
	};

	const cartItem = lines[productVariantId] || {};
	const isLineToCreate = linesToCreate[productVariantId];

	const dispatch = useDispatch();

	handleBeforeUnload(pendingChanges);

	const showSaveToast = useSaveToast(onToastClick);


	const isButtonDisabled = stock === "unavailable" || saveInProgress || isLimittedOffer;

	const addProduct = async () => {
		if (!isActive || isButtonDisabled) return;

		if ((stock || product.variant?.stock) === "one-time") {
			toggleLoading();
			const response = await dispatch(addLineOnce(+productVariantId));

			if (response?.success) {
				toast.success(translation?.subscription?.changes_saved);
			} else {
				toast.error(response.message);
				toggleLoading();
			}
		} else {
			dispatch({
				type: CREATE_LINE_ITEM,
				payload: {
					variantShopifyId: +productVariantId,
					isUpsell,
					basePrice: product.variant.price,
					discountedPrice: product.variant.price,
					image: product?.image,
					weightInGrams: product?.variant?.weight === 0 ? 1 : product?.variant?.weight * 1000,
					name: product.shopifyTitle || product.title,
					quantity: 1,
					isDuplicated: false,
					customAttributes: lifetimeOffer?.enabled ? [{ key: lifeTimeOfferKey, value: "true" }] : [],
				}
			});

			if (!isCartOpen && !isStickyCartOpen && !isCollection)
				showSaveToast(true);
		}

		trackAddToSubscriptionCart(product);
		callback?.();
	};

	if (cartItem?.id || isLineToCreate)
		return null;

	if (cartItem?.updating)
		return <Loader className="!size-4.5" />;

	return (
		<Tooltip
			containerClass="w-full"
			className="!translate-x-0 !mb-0 w-full"
			message={translation.subscription?.bundle_limit_offer_message} enabled={isLimittedOffer}>
			<div className="w-full" onClick={() => isDispatched && dispatch({ type: SET_HIGHLIGHT_BANNER, payload: true })}>
				<button
					disabled={isButtonDisabled}
					className={clsx(
						(!isSubscriptionPage || isUpsell) && "text-sm md:text-base mt-2",
						className, saveInProgress && "opacity-20 pointer-events-none",
						isSubscriptionPage && !isUpsell ? "bg-beige rounded-lg md:w-full w-10/12 py-1 text-h3/5 md:text-3xl/5 md:mt-2" :
							"bg-black rounded-6xl text-white py-1.5 md:py-2 w-full",
						(isButtonDisabled || isDispatched) && "opacity-50 pointer-events-none",
					)}
					{...(!isSubscriptionPage && { style: { backgroundColor } })}
					onClick={addProduct} aria-label="Quantity">{text ? text : isSubscriptionPage ? "+" : translation.product?.addToCart}
				</button>
			</div>
		</Tooltip>
	);
};

export default AddToSubscriptionCart;
